/**
 * React Twilio Dialer Component
 * Google Voice-style dialer with full call state machine and in-call controls
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { Device } from '@twilio/voice-sdk';
import { parsePhoneNumberFromString, AsYouType } from 'libphonenumber-js';
import useSessionPresenceWebSocket from '../utils/useSessionPresenceWebSocket';
import { authFetch } from '../auth';
import './TwilioDialer.css';

// Strip everything that isn't a digit or leading +. Used for pasted text and
// for extracting the canonical raw value from a formatted display string.
const cleanPhoneInput = (raw) => {
  if (!raw) return '';
  // Drop common URI prefixes / extension suffixes before extracting digits
  var s = String(raw)
    .replace(/^(tel:|callto:|sip:|sms:)/i, '')
    .replace(/(?:[xX]|ext\.?|extension)\s*\d+\s*$/i, '')
    .trim();
  // Preserve a leading + if present (anywhere — paste sometimes has spaces)
  var hasPlus = /^\s*\+/.test(s) || /\+(?=\d)/.test(s);
  var digits = s.replace(/\D/g, '');
  var out = hasPlus ? '+' + digits : digits;
  // Self-correct the common "+ then 10 digits" pattern (user typed/pasted +14086030548
  // minus the country code, or saved that way). Without this AsYouType reads +40 as
  // Romania and the dialer shows "+40 8603 0548" which won't dial.
  if (/^\+\d{10}$/.test(out)) out = '+1' + out.substring(1);
  return out;
};

// Call states
const CALL_STATES = {
  IDLE: 'idle',
  CONNECTING: 'connecting',
  RINGING: 'ringing',
  CONNECTED: 'connected',
  DISCONNECTED: 'disconnected',
  INCOMING: 'incoming',
};

const TwilioDialer = ({ user, onCallStatusChange }) => {
  const location = useLocation();
  // Core state
  const [callState, setCallState] = useState(CALL_STATES.IDLE);
  const [deviceReady, setDeviceReady] = useState(false);
  const [numberDialed, setNumberDialed] = useState('');
  const [originNumber, setOriginNumber] = useState('');
  const [callDuration, setCallDuration] = useState(0);
  const [statusMessage, setStatusMessage] = useState('Initializing...');

  // In-call state
  const [isMuted, setIsMuted] = useState(false);
  const [isHeld, setIsHeld] = useState(false);
  const [showDtmfKeypad, setShowDtmfKeypad] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingPending, setRecordingPending] = useState(false);
  const recordingSidRef = useRef(null);

  // Incoming call state
  const [incomingCallerInfo, setIncomingCallerInfo] = useState('');

  // Live contact-name lookup
  const [contactMatch, setContactMatch] = useState(null);

  // Contacts picker
  const [contacts, setContacts] = useState([]);
  const [contactsOpen, setContactsOpen] = useState(false);
  const [contactsFilter, setContactsFilter] = useState('');
  const contactsTriggerRef = useRef(null);

  // Quick SMS
  const [smsTemplatesOpen, setSmsTemplatesOpen] = useState(false);
  const [smsSending, setSmsSending] = useState(false);
  const [smsSent, setSmsSent] = useState(false);
  const smsTriggerRef = useRef(null);

  // Default country derived from the selected origin number — fallback US
  const defaultCountry = useMemo(() => {
    if (!originNumber) return 'US';
    var s = String(originNumber);
    if (!s.startsWith('+')) s = '+' + s.replace(/\D/g, '');
    var p = parsePhoneNumberFromString(s);
    return (p && p.country) || 'US';
  }, [originNumber]);

  // Parse current input. `e164` is what we hand to Twilio; `valid` gates the Call button.
  const parsed = useMemo(() => {
    var raw = cleanPhoneInput(numberDialed);
    if (!raw) return { valid: false, e164: null, formatted: '' };
    var p = parsePhoneNumberFromString(raw, defaultCountry);
    return {
      valid: !!(p && p.isValid()),
      e164: p ? p.number : null, // E.164
      formatted: p ? p.formatInternational() : raw,
    };
  }, [numberDialed, defaultCountry]);

  // Refs
  const deviceRef = useRef(null);
  const callRef = useRef(null);
  const durationIntervalRef = useRef(null);
  useSessionPresenceWebSocket(!!user, 'Dialer');

  // Load contacts on mount
  useEffect(() => {
    authFetch('/api/contacts').then(r => r.json()).then(d => {
      setContacts((d.contacts || d || []).map(function(c) { return { name: c.name || c.displayName || '', phone: c.phone || c.phoneNumber || '' }; }).filter(function(c) { return c.phone; }));
    }).catch(() => {});
  }, []);

  var sendQuickSms = async function(text) {
    if (!parsed.e164 || !originNumber || smsSending) return;
    setSmsSending(true);
    try {
      await authFetch('/api/conversations/send', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to: parsed.e164, from: originNumber, body: text })
      });
      setSmsSent(true);
    } catch(e) {}
    setSmsSending(false);
  };

  // Close contacts popover on outside click
  useEffect(() => {
    if (!contactsOpen) return;
    var handler = function(e) {
      if (contactsTriggerRef.current && contactsTriggerRef.current.contains(e.target)) return;
      var pop = document.querySelector('.contacts-popover');
      if (pop && pop.contains(e.target)) return;
      setContactsOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return function() { document.removeEventListener('mousedown', handler); };
  }, [contactsOpen]);

  useEffect(() => {
    if (!smsTemplatesOpen) return;
    var handler = function(e) {
      if (smsTriggerRef.current && smsTriggerRef.current.contains(e.target)) return;
      var pop = document.querySelector('.sms-templates-popover');
      if (pop && pop.contains(e.target)) return;
      setSmsTemplatesOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return function() { document.removeEventListener('mousedown', handler); };
  }, [smsTemplatesOpen]);

  // Initialize Twilio Device
  useEffect(() => {
    if (user && user.freenumbers && user.freenumbers.length > 0) {
      // Restore the last-used origin if it's still one of the user's numbers
      var remembered = localStorage.getItem('nf_last_origin');
      var initialOrigin = (remembered && user.freenumbers.indexOf(remembered) !== -1)
        ? remembered
        : user.freenumbers[0];
      setOriginNumber(initialOrigin);
      initializeTwilioDevice();
    }

    return () => {
      if (deviceRef.current) {
        deviceRef.current.destroy();
      }
      stopCallTimer();
    };
  }, [user]);

  // Persist the selected origin so the same number is pre-selected next session
  useEffect(() => {
    if (originNumber) {
      try { localStorage.setItem('nf_last_origin', originNumber); } catch (e) {}
    }
  }, [originNumber]);

  // Debounced contact-name lookup on the parsed E.164 — shows "Calling: <name>" above the input
  useEffect(() => {
    if (!parsed.e164 || callState !== CALL_STATES.IDLE) {
      setContactMatch(null);
      return undefined;
    }
    var cancelled = false;
    var t = setTimeout(async () => {
      try {
        var resp = await authFetch('/api/contacts/lookup', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ phone: parsed.e164 }),
        });
        var data = await resp.json();
        if (!cancelled) setContactMatch(resp.ok && data.found ? data.name : null);
      } catch (e) {
        if (!cancelled) setContactMatch(null);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [parsed.e164, callState]);

  // Type-ahead: match contacts by name as user types alpha
  var nameMatches = useMemo(function() {
    if (callState !== CALL_STATES.IDLE || !numberDialed || numberDialed.length < 2) return [];
    var raw = numberDialed.trim().toLowerCase();
    // Only trigger if there's at least one letter character
    if (!/[a-z]/i.test(raw)) return [];
    return contacts.filter(function(c) {
      return c.name.toLowerCase().includes(raw) || c.phone.includes(numberDialed.replace(/\D/g,''));
    }).slice(0, 5);
  }, [numberDialed, contacts, callState]);

  // Notify parent of call status changes
  useEffect(() => {
    if (onCallStatusChange) {
      onCallStatusChange(callState, callState === CALL_STATES.CONNECTED, callDuration);
    }
  }, [callState, callDuration, onCallStatusChange]);

  // Auto-reset from disconnected to idle
  useEffect(() => {
    if (callState === CALL_STATES.DISCONNECTED) {
      const timer = setTimeout(() => {
        setCallState(CALL_STATES.IDLE);
        setStatusMessage('Ready');
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [callState]);

  // Pre-fill dialer from Contact-page nav OR from URL params so links like
  //   /dialer?tel=+14155551234   (browser tel: handler route)
  //   /dialer?to=+14155551234
  //   /dialer?number=+14155551234
  // populate the recipient. Without this, tel: protocol handlers that route into the app
  // would land on /dialer with the number stuck in the URL and an empty input.
  useEffect(() => {
    var cb = location.state && location.state.callbackNumber;
    if (!cb) {
      try {
        var qs = new URLSearchParams(location.search || '');
        cb = qs.get('tel') || qs.get('to') || qs.get('number') || qs.get('phone');
      } catch(e) {}
    }
    if (cb) {
      // Strip "tel:" / "sms:" prefix that some OS handlers pass through verbatim.
      var stripped = String(cb).replace(/^(tel:|callto:|sip:|sms:)/i, '');
      setNumberDialed(stripped.replace(/[^\d+]/g, ''));
      window.history.replaceState({}, '', location.pathname);
    }
  }, [location.state, location.search, location.pathname]);

  const initializeTwilioDevice = async () => {
    try {
      setStatusMessage('Connecting...');

      const response = await authFetch('/auth0/ttoken/', {});

      if (!response.ok) {
        throw new Error('Failed to get Twilio token');
      }

      const twilioToken = await response.text();

      const newDevice = new Device(twilioToken, {
        codecPreferences: ['opus', 'pcmu'],
        fakeLocalDTMF: true,
        enableRingingState: true,
      });

      newDevice.on('registered', () => {
        console.log('Twilio Device registered');
        setDeviceReady(true);
        setStatusMessage('Ready');
      });

      newDevice.on('error', (error) => {
        console.error('Twilio Device error:', error);
        setStatusMessage(`Error: ${error.message}`);
        setDeviceReady(false);
      });

      newDevice.on('incoming', (incomingCall) => {
        console.log('Incoming call from:', incomingCall.parameters.From);
        callRef.current = incomingCall;
        setIncomingCallerInfo(incomingCall.parameters.From || 'Unknown');
        setCallState(CALL_STATES.INCOMING);
        setStatusMessage('Incoming call');
        setupCallHandlers(incomingCall);
      });

      await newDevice.register();
      deviceRef.current = newDevice;
    } catch (error) {
      console.error('Failed to initialize Twilio Device:', error);
      setStatusMessage(`Connection failed`);
    }
  };

  const setupCallHandlers = (call) => {
    call.on('ringing', () => {
      setCallState(CALL_STATES.RINGING);
      setStatusMessage('Ringing...');
    });

    call.on('accept', () => {
      setCallState(CALL_STATES.CONNECTED);
      setStatusMessage('Connected');
      setIsMuted(false);
      setIsHeld(false);
      setShowDtmfKeypad(false);
      startCallTimer();
    });

    call.on('disconnect', () => {
      setCallState(CALL_STATES.DISCONNECTED);
      setStatusMessage('Call ended');
      resetCallState();
    });

    call.on('cancel', () => {
      setCallState(CALL_STATES.DISCONNECTED);
      setStatusMessage('Call cancelled');
      resetCallState();
    });

    call.on('reject', () => {
      setCallState(CALL_STATES.DISCONNECTED);
      setStatusMessage('Call rejected');
      resetCallState();
    });

    call.on('error', (error) => {
      console.error('Call error:', error);
      setCallState(CALL_STATES.DISCONNECTED);
      setStatusMessage(`Call error`);
      resetCallState();
    });

    call.on('reconnecting', () => {
      setStatusMessage('Reconnecting...');
    });

    call.on('reconnected', () => {
      setStatusMessage('Connected');
    });
  };

  const resetCallState = () => {
    callRef.current = null;
    setIsMuted(false);
    setIsHeld(false);
    setShowDtmfKeypad(false);
    setIncomingCallerInfo('');
    setIsRecording(false);
    setRecordingPending(false);
    recordingSidRef.current = null;
    stopCallTimer();
  };

  const startCallTimer = () => {
    setCallDuration(0);
    durationIntervalRef.current = setInterval(() => {
      setCallDuration(prev => prev + 1);
    }, 1000);
  };

  const stopCallTimer = () => {
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
      durationIntervalRef.current = null;
    }
    setCallDuration(0);
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Dial pad digit handler
  const dialDigit = useCallback((digit) => {
    if (callState === CALL_STATES.CONNECTED && callRef.current) {
      // DTMF mode — send digit to active call
      try {
        callRef.current.sendDigits(digit);
      } catch (error) {
        console.error('Failed to send DTMF:', error);
      }
    } else if (callState === CALL_STATES.IDLE) {
      // Normal dialing — append to number
      setNumberDialed(prev => prev + digit);
    }
  }, [callState]);

  const clearNumber = () => setNumberDialed('');
  // Backspace drops the last digit (the displayed value may be formatted with
  // spaces/parens, so slicing the raw cleaned form gives a predictable feel).
  const backspace = () => setNumberDialed(prev => {
    var raw = cleanPhoneInput(prev);
    return raw.slice(0, -1);
  });

  // While the user is DELETING, hold the input to the exact string the keyboard
  // produced instead of reformatting. Rewriting the DOM value mid-delete jumps the
  // caret to the end and desyncs iOS's keyboard buffer — perceived as backspace
  // RE-INSERTING the last digit. Stale overrides (digits no longer matching state)
  // are ignored automatically, so keypad taps / paste / contact picks reformat.
  const [displayOverride, setDisplayOverride] = useState(null);

  // Live formatter applied to whatever the user has typed/pasted so far.
  const displayedNumber = useMemo(() => {
    if (displayOverride !== null && cleanPhoneInput(displayOverride) === cleanPhoneInput(numberDialed)) {
      return displayOverride;
    }
    if (!numberDialed) return '';
    var raw = cleanPhoneInput(numberDialed);
    if (!raw) return '';
    // AsYouType handles partial input gracefully and respects the leading + when present.
    return new AsYouType(defaultCountry).input(raw);
  }, [numberDialed, displayOverride, defaultCountry]);

  // Keep the backspace-detector in sync with whatever's currently rendered in the input,
  // including changes from dial-pad taps, paste, contacts, and location.state.
  useEffect(() => { lastInputValueRef.current = displayedNumber; }, [displayedNumber]);

  // Track the last value the input rendered so we can detect when the user backspaced a
  // formatting char (space/paren/dash) — in that case AsYouType immediately re-adds it
  // and the user perceives backspace as broken. We drop one digit in that case.
  const lastInputValueRef = useRef('');
  // Backspace is handled EXPLICITLY: with a formatted controlled input, letting the
  // browser mutate the string and then reconciling (change-event path) proved fragile
  // across keyboards (iOS echo re-inserted the last digit). When the caret is at the
  // end with no selection — the normal dialer case — we preventDefault and drop the
  // last digit in state ourselves, exactly like the on-screen ⌫ button. Mid-string
  // edits/selections still fall through to handleNumberChange.
  const handleKeyDown = (e) => {
    if (e.key !== 'Backspace') return;
    var el = e.target;
    var atEnd = el.selectionStart === el.selectionEnd && el.selectionEnd === el.value.length;
    if (!atEnd) return;
    e.preventDefault();
    setDisplayOverride(null);
    setNumberDialed(prev => cleanPhoneInput(prev).slice(0, -1));
  };

  const handleNumberChange = (e) => {
    var nextVal = e.target.value;
    var prevVal = lastInputValueRef.current;
    var nextDigits = cleanPhoneInput(nextVal);
    var prevDigits = cleanPhoneInput(prevVal);
    var deleting = nextVal.length < prevVal.length;
    if (deleting && nextDigits === prevDigits) {
      // Backspaced a pure formatting char (space/paren/dash) — drop a real digit
      // and let the formatter re-render, otherwise the char instantly reappears.
      nextDigits = nextDigits.slice(0, -1);
      setDisplayOverride(null);
    } else if (deleting) {
      // Deleting digits: keep the DOM string EXACTLY as the keyboard left it
      // (no reformat → no caret jump, no iOS duplicate-digit echo).
      setDisplayOverride(nextVal);
    } else {
      setDisplayOverride(null);
    }
    lastInputValueRef.current = nextVal;
    setNumberDialed(nextDigits);
  };

  // Paste-and-clean: strip "tel:" / "ext. 5" / spaces / parens / dashes; if the result parses
  // as a valid number we store the canonical E.164, otherwise keep the cleaned digits.
  const handlePaste = (e) => {
    e.preventDefault();
    var text = (e.clipboardData || window.clipboardData).getData('text') || '';
    var cleaned = cleanPhoneInput(text);
    if (!cleaned) return;
    var p = parsePhoneNumberFromString(cleaned, defaultCountry);
    setNumberDialed(p && p.isValid() ? p.number : cleaned);
  };

  // Make outbound call
  const makeCall = async () => {
    if (callState !== CALL_STATES.IDLE || !deviceRef.current || !parsed.valid) return;

    try {
      setCallState(CALL_STATES.CONNECTING);
      setStatusMessage('Connecting...');

      const call = await deviceRef.current.connect({
        params: {
          PhoneNumber: parsed.e164,
          CallFromNumber: originNumber,
        }
      });

      setNumberDialed('');
      setContactMatch(null);
      callRef.current = call;
      setupCallHandlers(call);
    } catch (error) {
      console.error('Call failed:', error);
      setCallState(CALL_STATES.DISCONNECTED);
      setStatusMessage('Call failed');
      resetCallState();
    }
  };

  // Answer incoming call
  const answerCall = () => {
    if (callRef.current && callState === CALL_STATES.INCOMING) {
      callRef.current.accept();
    }
  };

  // Reject incoming call
  const rejectCall = () => {
    if (callRef.current && callState === CALL_STATES.INCOMING) {
      callRef.current.reject();
    }
  };

  // End active call
  const endCall = () => {
    if (callRef.current) {
      callRef.current.disconnect();
    }
  };

  // Toggle mute
  const toggleMute = () => {
    if (callRef.current && callState === CALL_STATES.CONNECTED) {
      const newMuted = !isMuted;
      callRef.current.mute(newMuted);
      setIsMuted(newMuted);
    }
  };

  // Toggle hold (visual + mute, SDK has no native hold)
  const toggleHold = () => {
    if (callRef.current && callState === CALL_STATES.CONNECTED) {
      const newHeld = !isHeld;
      callRef.current.mute(newHeld);
      setIsHeld(newHeld);
      setIsMuted(newHeld);
    }
  };

  // Toggle DTMF keypad overlay
  const toggleDtmfKeypad = () => {
    setShowDtmfKeypad(prev => !prev);
  };

  // Toggle call recording (Twilio dual-channel) with one-time legal disclosure per session
  const toggleRecording = async () => {
    if (callState !== CALL_STATES.CONNECTED || !callRef.current || recordingPending) return;
    const callSid = callRef.current.parameters && callRef.current.parameters.CallSid;
    if (!callSid) {
      setStatusMessage('Recording unavailable (no call SID yet)');
      return;
    }

    if (!isRecording) {
      if (!sessionStorage.getItem('nf_recording_disclosure_ack')) {
        const ok = window.confirm(
          'Recording this call.\n\n' +
          'You are responsible for notifying all participants and complying with local recording laws ' +
          '(many jurisdictions require all-party consent). Continue?'
        );
        if (!ok) return;
        sessionStorage.setItem('nf_recording_disclosure_ack', '1');
      }
      setRecordingPending(true);
      try {
        const resp = await authFetch('/api/callhistory/recording/live/start', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ callSid }),
        });
        const data = await resp.json();
        if (!resp.ok) throw new Error(data.error || 'Failed to start recording');
        recordingSidRef.current = data.recordingSid;
        setIsRecording(true);
      } catch (err) {
        console.error('Start recording failed:', err);
        setStatusMessage('Recording failed: ' + err.message);
      } finally {
        setRecordingPending(false);
      }
    } else {
      const recordingSid = recordingSidRef.current;
      if (!recordingSid) { setIsRecording(false); return; }
      setRecordingPending(true);
      try {
        const resp = await authFetch('/api/callhistory/recording/live/stop', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ callSid, recordingSid }),
        });
        const data = await resp.json();
        if (!resp.ok) throw new Error(data.error || 'Failed to stop recording');
        recordingSidRef.current = null;
        setIsRecording(false);
      } catch (err) {
        console.error('Stop recording failed:', err);
        setStatusMessage('Stop recording failed: ' + err.message);
      } finally {
        setRecordingPending(false);
      }
    }
  };

  const isCallActive = [CALL_STATES.CONNECTING, CALL_STATES.RINGING, CALL_STATES.CONNECTED].includes(callState);

  // Dial pad button data
  const dialPadButtons = [
    [{ digit: '1', letters: '' }, { digit: '2', letters: 'ABC' }, { digit: '3', letters: 'DEF' }],
    [{ digit: '4', letters: 'GHI' }, { digit: '5', letters: 'JKL' }, { digit: '6', letters: 'MNO' }],
    [{ digit: '7', letters: 'PQRS' }, { digit: '8', letters: 'TUV' }, { digit: '9', letters: 'WXYZ' }],
    [{ digit: '*', letters: '' }, { digit: '0', letters: '+' }, { digit: '#', letters: '' }],
  ];

  // No phone numbers — setup required
  if (!user || !user.freenumbers || user.freenumbers.length === 0) {
    return (
      <div className="dialer-container">
        <div className="setup-required">
          <h3>Phone Setup Required</h3>
          <p>Please complete your NumFree phone number setup to use the dialer.</p>
        </div>
      </div>
    );
  }

  // Incoming call overlay
  if (callState === CALL_STATES.INCOMING) {
    return (
      <div className="dialer-container">
        <div className="incoming-call-overlay">
          <div className="incoming-avatar">
            <svg viewBox="0 0 24 24" width="48" height="48" fill="white">
              <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
            </svg>
          </div>
          <div className="incoming-caller">{incomingCallerInfo}</div>
          <div className="incoming-label">Incoming call</div>
          <div className="incoming-actions">
            <button className="incoming-reject" onClick={rejectCall}>
              <svg viewBox="0 0 24 24" width="28" height="28" fill="white">
                <path d="M12 9c-1.6 0-3.15.25-4.6.72v3.1c0 .39-.23.74-.56.9-.98.49-1.87 1.12-2.66 1.85-.18.18-.43.28-.7.28-.28 0-.53-.11-.71-.29L.29 13.08c-.18-.17-.29-.42-.29-.7 0-.28.11-.53.29-.71C3.34 8.78 7.46 7 12 7s8.66 1.78 11.71 4.67c.18.18.29.43.29.71 0 .28-.11.53-.29.71l-2.48 2.48c-.18.18-.43.29-.71.29-.27 0-.52-.11-.7-.28-.79-.74-1.69-1.36-2.67-1.85-.33-.16-.56-.5-.56-.9v-3.1C15.15 9.25 13.6 9 12 9z"/>
              </svg>
            </button>
            <button className="incoming-answer" onClick={answerCall}>
              <svg viewBox="0 0 24 24" width="28" height="28" fill="white">
                <path d="M20.01 15.38c-1.23 0-2.42-.2-3.53-.56-.35-.12-.74-.03-1.01.24l-1.57 1.97c-2.83-1.35-5.48-3.9-6.89-6.83l1.95-1.66c.27-.28.35-.67.24-1.02-.37-1.11-.56-2.3-.56-3.53 0-.54-.45-.99-.99-.99H4.19C3.65 3 3 3.24 3 3.99 3 13.28 10.73 21 20.01 21c.71 0 .99-.63.99-1.18v-3.45c0-.54-.45-.99-.99-.99z"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Active call screen
  if (isCallActive) {
    return (
      <div className="dialer-container">
        <div className="active-call-screen">
          <div className="active-call-info">
            <div className="active-call-number">{numberDialed || 'Call'}</div>
            <div className="active-call-status">{statusMessage}</div>
            {callState === CALL_STATES.CONNECTED && (
              <div className="active-call-duration">{formatDuration(callDuration)}</div>
            )}
          </div>

          {/* DTMF keypad overlay */}
          {showDtmfKeypad && callState === CALL_STATES.CONNECTED && (
            <div className="dtmf-keypad">
              {dialPadButtons.map((row, rowIndex) => (
                <div key={rowIndex} className="dial-row">
                  {row.map(button => (
                    <button
                      key={button.digit}
                      className="dial-button dtmf-button"
                      onClick={() => dialDigit(button.digit)}
                    >
                      <div className="digit">{button.digit}</div>
                      {button.letters && <div className="letters">{button.letters}</div>}
                    </button>
                  ))}
                </div>
              ))}
              <button className="dtmf-close" onClick={toggleDtmfKeypad}>Hide keypad</button>
            </div>
          )}

          {/* In-call controls grid */}
          {!showDtmfKeypad && (
            <div className="incall-controls">
              <button
                className={`incall-btn ${isMuted ? 'active' : ''}`}
                onClick={toggleMute}
                disabled={callState !== CALL_STATES.CONNECTED}
              >
                <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
                  {isMuted ? (
                    <path d="M19 11h-1.7c0 .74-.16 1.43-.43 2.05l1.23 1.23c.56-.98.9-2.09.9-3.28zm-4.02.17c0-.06.02-.11.02-.17V5c0-1.66-1.34-3-3-3S9 3.34 9 5v.18l5.98 5.99zM4.27 3L3 4.27l6.01 6.01V11c0 1.66 1.33 3 2.99 3 .22 0 .44-.03.65-.08l1.66 1.66c-.71.33-1.5.52-2.31.52-2.76 0-5.3-2.1-5.3-5.1H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c.91-.13 1.77-.45 2.54-.9L19.73 21 21 19.73 4.27 3z"/>
                  ) : (
                    <path d="M12 14c1.66 0 2.99-1.34 2.99-3L15 5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.48 6-3.3 6-6.72h-1.7z"/>
                  )}
                </svg>
                <span>{isMuted ? 'Unmute' : 'Mute'}</span>
              </button>

              <button
                className={`incall-btn ${showDtmfKeypad ? 'active' : ''}`}
                onClick={toggleDtmfKeypad}
                disabled={callState !== CALL_STATES.CONNECTED}
              >
                <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
                  <path d="M12 19c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zM6 1c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm12-8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm-6 8c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm6 0c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0-6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm-6 0c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0-6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/>
                </svg>
                <span>Keypad</span>
              </button>

              <button className="incall-btn" disabled>
                <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
                  <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
                </svg>
                <span>Audio</span>
              </button>

              <button
                className={`incall-btn ${isHeld ? 'active' : ''}`}
                onClick={toggleHold}
                disabled={callState !== CALL_STATES.CONNECTED}
              >
                <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
                  <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
                </svg>
                <span>{isHeld ? 'Resume' : 'Hold'}</span>
              </button>

              <button
                className={`incall-btn ${isRecording ? 'active recording' : ''}`}
                onClick={toggleRecording}
                disabled={callState !== CALL_STATES.CONNECTED || recordingPending}
                title={isRecording ? 'Stop recording' : 'Start recording'}
              >
                <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
                  {isRecording ? (
                    <path d="M6 6h12v12H6z"/>
                  ) : (
                    <circle cx="12" cy="12" r="6"/>
                  )}
                </svg>
                <span>{recordingPending ? '…' : (isRecording ? 'Stop' : 'Record')}</span>
              </button>
            </div>
          )}

          {/* End call button */}
          <div className="end-call-row">
            <button className="end-call-btn" onClick={endCall}>
              <svg viewBox="0 0 24 24" width="28" height="28" fill="white">
                <path d="M12 9c-1.6 0-3.15.25-4.6.72v3.1c0 .39-.23.74-.56.9-.98.49-1.87 1.12-2.66 1.85-.18.18-.43.28-.7.28-.28 0-.53-.11-.71-.29L.29 13.08c-.18-.17-.29-.42-.29-.7 0-.28.11-.53.29-.71C3.34 8.78 7.46 7 12 7s8.66 1.78 11.71 4.67c.18.18.29.43.29.71 0 .28-.11.53-.29.71l-2.48 2.48c-.18.18-.43.29-.71.29-.27 0-.52-.11-.7-.28-.79-.74-1.69-1.36-2.67-1.85-.33-.16-.56-.5-.56-.9v-3.1C15.15 9.25 13.6 9 12 9z"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Idle mode — dial pad
  return (
    <div className="dialer-container">
      {/* Status */}
      <div className="call-status">
        <div data-testid="dialer-status" className="status-text">{statusMessage}</div>
      </div>

      {/* Origin number selector */}
      <div className="number-input-section">
        <div className="origin-number">
          <label>Call from:</label>
          <select
            data-testid="dialer-from-select"
            value={originNumber}
            onChange={(e) => setOriginNumber(e.target.value)}
          >
            {user.freenumbers.map(number => (
              <option key={number} value={number}>{number}</option>
            ))}
          </select>
        </div>

         {/* Number input */}
         <div className="destination-input">
          <button type="button" className="contacts-lookup-btn" ref={contactsTriggerRef} onClick={() => { setContactsOpen(o => !o); setContactsFilter(''); }} title="Contacts" aria-label="Look up contacts">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></svg>
          </button>
           <input
            data-testid="dialer-input"
            type="text"
            inputMode="tel"
            value={displayedNumber}
            onChange={handleNumberChange}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            placeholder="Enter a number"
            autoComplete="off"
          />
          {numberDialed && (
            <div className="input-controls">
              <button onClick={backspace} aria-label="Backspace">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="#5f6368">
                  <path d="M22 3H7c-.69 0-1.23.35-1.59.88L0 12l5.41 8.11c.36.53.9.89 1.59.89h15c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-3 12.59L17.59 17 14 13.41 10.41 17 9 15.59 12.59 12 9 8.41 10.41 7 14 10.59 17.59 7 19 8.41 15.41 12 19 15.59z"/>
                </svg>
              </button>
              <button type="button" ref={smsTriggerRef} onClick={() => { setSmsTemplatesOpen(o => !o); setSmsSent(false); }} aria-label="Send SMS" style={{marginLeft:4}}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#5f6368" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
              </button>
            </div>
          )}
        </div>
        {numberDialed && !nameMatches.length && (
          <div className="dial-hint" data-testid="dialer-hint">
            {contactMatch
              ? <span className="dial-hint-name">{contactMatch}</span>
              : (parsed.valid
                  ? <span className="dial-hint-ok">{parsed.formatted}</span>
                  : <span className="dial-hint-invalid">Number is incomplete</span>)}
          </div>
        )}
        {nameMatches.length > 0 && (
          <div className="dial-name-matches">
            {nameMatches.map(function(c) { return (
              <button key={c.phone} type="button" className="dial-name-match" onClick={() => setNumberDialed(c.phone)}>
                <span className="dial-name-match-name">{c.name}</span>
                <span className="dial-name-match-phone">{c.phone}</span>
              </button>
            );})}
          </div>
        )}
      </div>

      {/* Dial Pad */}
      <div className="dial-pad">
        {dialPadButtons.map((row, rowIndex) => (
          <div key={rowIndex} className="dial-row">
            {row.map(button => (
              <button
                key={button.digit}
                className="dial-button"
                onClick={() => dialDigit(button.digit)}
                disabled={!deviceReady}
              >
                <div className="digit">{button.digit}</div>
                {button.letters && <div className="letters">{button.letters}</div>}
              </button>
            ))}
          </div>
        ))}
      </div>

      {/* Call button */}
      <div className="call-controls">
        <button
          data-testid="dialer-call-btn"
          className="call-button"
          onClick={makeCall}
          disabled={!deviceReady || !parsed.valid}
          title={!parsed.valid && numberDialed ? 'Number is incomplete' : undefined}
        >
          <svg viewBox="0 0 24 24" width="28" height="28" fill="white">
            <path d="M20.01 15.38c-1.23 0-2.42-.2-3.53-.56-.35-.12-.74-.03-1.01.24l-1.57 1.97c-2.83-1.35-5.48-3.9-6.89-6.83l1.95-1.66c.27-.28.35-.67.24-1.02-.37-1.11-.56-2.3-.56-3.53 0-.54-.45-.99-.99-.99H4.19C3.65 3 3 3.24 3 3.99 3 13.28 10.73 21 20.01 21c.71 0 .99-.63.99-1.18v-3.45c0-.54-.45-.99-.99-.99z"/>
          </svg>
        </button>
      </div>
      {contactsOpen && createPortal(
        <div className="contacts-popover" role="menu" style={{position:'fixed',top:contactsTriggerRef.current ? contactsTriggerRef.current.getBoundingClientRect().bottom + 4 : 200,left:contactsTriggerRef.current ? contactsTriggerRef.current.getBoundingClientRect().left : 16,width:280,maxHeight:360,zIndex:2000,background:'#fff',border:'1px solid #e0e0e0',borderRadius:12,boxShadow:'0 8px 24px rgba(0,0,0,0.18)',overflow:'hidden',display:'flex',flexDirection:'column'}}>
          <div style={{padding:'8px',borderBottom:'1px solid #eee'}}>
            <input type="text" className="contacts-popover-filter" placeholder="Search contacts..." value={contactsFilter} onChange={e => setContactsFilter(e.target.value)} autoFocus style={{width:'100%',border:'1px solid #e0e0e0',borderRadius:8,padding:'8px 12px',fontSize:14,outline:'none',boxSizing:'border-box'}} />
          </div>
          <div style={{overflowY:'auto',flex:1}} onClick={() => setContactsOpen(false)}>
            {contacts.filter(function(c) { var q = contactsFilter.toLowerCase(); return !q || c.name.toLowerCase().includes(q) || c.phone.includes(q); }).map(function(c) { return (
              <button key={c.phone} type="button" onClick={() => { setNumberDialed(c.phone); }} style={{width:'100%',display:'flex',justifyContent:'space-between',alignItems:'center',padding:'10px 14px',border:'none',background:'transparent',cursor:'pointer',textAlign:'left',fontSize:14,borderBottom:'1px solid #f5f5f5'}}>
                <span style={{fontWeight:500,color:'#333'}}>{c.name}</span>
                <span style={{fontSize:13,color:'#999'}}>{c.phone}</span>
              </button>
            );})}
            {contactsFilter && contacts.filter(function(c) { var q = contactsFilter.toLowerCase(); return c.name.toLowerCase().includes(q) || c.phone.includes(q); }).length === 0 && (
              <div style={{padding:16,textAlign:'center',color:'#999',fontSize:13}}>No contacts found</div>
            )}
          </div>
        </div>,
        document.body
      )}
      {smsTemplatesOpen && createPortal(
        <div className="sms-templates-popover" role="menu" style={{position:'fixed',top:smsTriggerRef.current ? smsTriggerRef.current.getBoundingClientRect().bottom + 4 : 200,right:smsTriggerRef.current ? window.innerWidth - smsTriggerRef.current.getBoundingClientRect().right : 16,width:260,zIndex:2000,background:'#fff',border:'1px solid #e0e0e0',borderRadius:12,boxShadow:'0 8px 24px rgba(0,0,0,0.18)',overflow:'hidden',padding:6}}>
          {smsSent ? (
            <div style={{padding:'16px',textAlign:'center',color:'#43a047',fontSize:14}}>SMS sent ✓</div>
          ) : (
            ['Tried to reach you, call me back','Tried to reach you, I will call back','Let me know best time to call you back'].map(function(t) { return (
              <button key={t} type="button" onClick={() => sendQuickSms(t)} disabled={smsSending} style={{width:'100%',padding:'10px 12px',border:'none',background:'transparent',cursor:'pointer',textAlign:'left',fontSize:14,color:'#333',borderRadius:8,borderBottom:'1px solid #f5f5f5'}}>
                {t}
              </button>
            );})
          )}
        </div>,
        document.body
      )}
    </div>
  );
};

export default TwilioDialer;
